import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { BreadCrumb } from "primereact/breadcrumb";
import Sidebar from "../../components/common/Sidebar";
import { useCart } from "../../contexts/CartProvider";
import { FaShoppingCart } from "react-icons/fa";
import CartPanel from "../../components/common/CartPanel";
import { useRCN } from "../../contexts/RCNContext";
import LoadingWrapper from "../../components/common/LoadingWrapper";
import { useToast } from "../../contexts/ToastContext";
import { useMenuAccess } from "../../contexts/MenuAccessContext";
import RouteAccessGuard from "../../components/RouteAccessGuard";



const routeMetadata = {
  // ---------------- Loyalty ----------------
  "/loyalty/dashboard": { title: "Loyalty Program - Dashboard", breadcrumbs: ["Loyalty Program", "Dashboard"] },
  "/loyalty/program": { title: "Loyalty Program - Program", breadcrumbs: ["Loyalty Program", "Program"] },
  "/loyalty/settings": { title: "Loyalty Program - Settings", breadcrumbs: ["Loyalty Program", "Settings"] },
  "/loyalty/analytics": { title: "Loyalty Program - Analytics", breadcrumbs: ["Loyalty Program", "Analytics"] },
  "/loyalty/reporting": { title: "Loyalty Program - Reporting", breadcrumbs: ["Loyalty Program", "Reporting"] },
  "/loyalty/segments": { title: "Loyalty Program - Segments", breadcrumbs: ["Loyalty Program", "Segments"] },
  "/loyalty/segments/create": { title: "Loyalty Program - Create Segment", breadcrumbs: ["Loyalty Program", "Segments", "Create"] },
  "/loyalty/segments/:id/update": { title: "Loyalty Program - Update Segment", breadcrumbs: ["Loyalty Program", "Segments", "Update"] },
  "/loyalty/segments/:id/customers": { title: "Loyalty Program - Segment Customers", breadcrumbs: ["Loyalty Program", "Segments", "Customers"] },
  "/loyalty/segments/:id/automations": { title: "Loyalty Program - Segment Automations", breadcrumbs: ["Loyalty Program", "Segments", "Automations"] },
  "/loyalty/automations": { title: "Loyalty Program - Automations", breadcrumbs: ["Loyalty Program", "Automations"] },
  "/loyalty/automations/create": { title: "Loyalty Program - Create Automation", breadcrumbs: ["Loyalty Program", "Automations", "Create"] },
  "/loyalty/automations/:automationId/edit": { title: "Loyalty Program - Edit Automation", breadcrumbs: ["Loyalty Program", "Automations", "Edit"] },

  // ---------------- Inventory ----------------
  "/inventory/dashboard": { title: "Inventory - Dashboard", breadcrumbs: ["Inventory", "Dashboard"] },
  "/inventory/items": { title: "Inventory - Items", breadcrumbs: ["Inventory", "Items"] },
  "/inventory/bulk-updates": { title: "Inventory - Bulk Updates", breadcrumbs: ["Inventory", "Bulk Updates"] },
  "/inventory/bulk-updates/:id": { title: "Inventory - Bulk Update Details", breadcrumbs: ["Inventory", "Bulk Updates", "View"] },
  "/inventory/bulk-updates/create": { title: "Inventory - Bulk Update Create", breadcrumbs: ["Inventory", "Bulk Updates", "Create"] },
  "/inventory/audit": { title: "Inventory Audit", breadcrumbs: ["Inventory", "Audit"] },
  "/inventory/reports": { title: "Inventory Reports", breadcrumbs: ["Inventory", "Reports"] },
  "/inventory/new": { title: "Inventory - Create Item", breadcrumbs: ["Inventory", "New Item"] },
  "/inventory/:itemId/edit": { title: "Inventory - Edit Item", breadcrumbs: ["Inventory", "Edit Item"] },

  // ---------------- Purchase Management ----------------
  "/purchase-management/dashboard": { title: "Purchase Management - Dashboard", breadcrumbs: ["Purchase Management", "Dashboard"] },
  "/purchase-management/vendors": { title: "Purchase Management - Vendors", breadcrumbs: ["Purchase Management", "Vendors"] },
  "/purchase-management/product-catalogs": { title: "Purchase Management - Product Catalogs", breadcrumbs: ["Purchase Management", "Product Catalogs"] },
  "/purchase-management/invoices": { title: "Purchase Management - Invoices", breadcrumbs: ["Purchase Management", "Invoices"] },
  "/purchase-management/invoices/:invoiceId": { title: "Purchase Management - Invoice Details", breadcrumbs: ["Purchase Management", "Invoices", "Invoice Details"] },
  "/purchase-management/settings": { title: "Purchase Management - Settings", breadcrumbs: ["Purchase Management", "Settings"] },
  "/purchase-management/vendors/:vendorId": { title: "Purchase Management - Vendor Details", breadcrumbs: ["Purchase Management", "Vendors", "Details"] },
  "/purchase-management/vendors/:vendorId/generate-po": { title: "Generate Purchase Order", breadcrumbs: ["Purchase Management", "Vendors", "Generate PO"] },
  "/purchase-management/purchase-orders": { title: "Purchase Orders", breadcrumbs: ["Purchase Management", "Purchase Orders"] },
  "/purchase-management/purchase-orders/:purchaseOrderId": { title: "Purchase Order Details", breadcrumbs: ["Purchase Management", "Purchase Orders", "Details"] },
  "/purchase-management/purchase-orders/:purchaseOrderId/reconcile": { title: "Reconcile Purchase Order", breadcrumbs: ["Purchase Management", "Purchase Orders", "Reconcile"] },
  "/whole-sales/retailers": { title: "Wholesale - Retailers", breadcrumbs: ["Wholesale", "Retailers"] },
  "/whole-sales/retailers/new": { title: "Wholesale - Add Retailer", breadcrumbs: ["Wholesale", "Retailers", "Add Retailer"] },
  "/whole-sales/retailers/:retailerId": { title: "Wholesale - Retailer Details", breadcrumbs: ["Wholesale", "Retailers", "Details"] },
  "/whole-sales/dashboard": { title: "Wholesale - Dashboard", breadcrumbs: ["Wholesale", "Dashboard"] },
  "/whole-sales/product-catalogs": { title: "Wholesale - Product Catalogs", breadcrumbs: ["Wholesale", "Product Catalogs"] },
  "/pricing": { title: "Pricing", breadcrumbs: ["Pricing"] },
  "/pricing/create": { title: "Create Price Group", breadcrumbs: ["Pricing", "Create Price Group"] },
  "/product-catalogs/create-po": { title: "Create Purchase Order", breadcrumbs: ["Purchase Orders", "Product Catalogs"] },
  "/whole-sales/invoices": { title: "Wholesale - Invoices", breadcrumbs: ["Wholesale", "Invoices"] },
  "/whole-sales/invoices/:invoiceId": { title: "Wholesale - Invoice Details", breadcrumbs: ["Wholesale", "Invoices", "Invoice Details"] },
  "/whole-sales/purchase-orders": { title: "Wholesale - Purchase Orders", breadcrumbs: ["Wholesale", "Purchase Orders"] },
  "/whole-sales/purchase-orders/:purchaseOrderId": { title: "Wholesale - Purchase Order Details", breadcrumbs: ["Wholesale", "Purchase Orders", "Details"] },
  "/whole-sales/purchase-orders/:purchaseOrderId/reconcile": { title: "Wholesale - Reconcile Purchase Order", breadcrumbs: ["Wholesale", "Purchase Orders", "Reconcile"] },
  "/whole-sales/purchase-orders/:purchaseOrderId/finalize": { title: "Wholesale - Finalize Invoice", breadcrumbs: ["Wholesale", "Purchase Orders", "Finalize"] },
  "/whole-sales/retailers/:retailerId/generate-po": { title: "Wholesale - Generate PO", breadcrumbs: ["Wholesale", "Retailers", "Generate PO"] },
  "/whole-sales/retailers/:retailerId/invoices/:invoiceId": { title: "Wholesale - Retailer Invoice Details", breadcrumbs: ["Wholesale", "Retailers", "Invoices", "Details"] },
  "/whole-sales/retailers/:retailerId/purchase-orders/:purchaseOrderId": { title: "Wholesale - Retailer Purchase Order Details", breadcrumbs: ["Wholesale", "Retailers", "Purchase Orders", "Details"] },
  "/whole-sales/settings": { title: "Wholesale - Settings", breadcrumbs: ["Wholesale", "Settings"] },

  // ---------------- Scan Data ----------------
  "/scan-data/dashboard": { title: "Scan Data Dashboard", breadcrumbs: ["Scan Data", "Dashboard"] },
  "/scan-data/rjr-discounts": { title: "RJR Discounts", breadcrumbs: ["Scan Data", "RJR Discounts"] },
  "/scan-data/accounts": { title: "Scan Data Accounts", breadcrumbs: ["Scan Data", "Accounts"] },
  "/scan-data/reports": { title: "Scan Data Reports", breadcrumbs: ["Scan Data", "Reports"] },
  "/scan-data/store-settings": { title: "Scan Data Store Settings", breadcrumbs: ["Scan Data", "Store Settings"] },
  "/scan-data/help": { title: "Scan Data Help", breadcrumbs: ["Scan Data", "Help"] },

  // ---------------- Communication ----------------
  "/communication/dashboard": { title: "Communication - Dashboard", breadcrumbs: ["Communication", "Dashboard"] },
  "/communication/analytics": { title: "Communication - Analytics", breadcrumbs: ["Communication", "Analytics"] },
  "/communication/campaigns": { title: "Communication - Campaigns", breadcrumbs: ["Communication", "Campaigns"] },

  // ---------------- Promotions / Coupons ----------------
  "/promotions/dashboard": { title: "Promotions - Dashboard", breadcrumbs: ["Promotions", "Dashboard"] },
  "/promotions/coupons": { title: "Coupons", breadcrumbs: ["Promotions", "Coupons"] },
  "/promotions/coupons/create": { title: "Create Coupon", breadcrumbs: ["Promotions", "Coupons", "Create"] },
  "/promotions/coupons/:couponId/update": { title: "Update Coupon", breadcrumbs: ["Promotions", "Coupons", "Update"] },
  "/promotions/coupons/:couponId": { title: "Coupon Details", breadcrumbs: ["Promotions", "Coupons", "Details"] },
  "/gift-kard/dashboard": { title: "Gift Card - Dashboard", breadcrumbs: ["Gift Card", "Dashboard"] },
  "/gift-kard/cards": { title: "Gift Card - Gift Cards", breadcrumbs: ["Gift Card", "Gift Cards"] },
  "/gift-kard/reports": { title: "Gift Card - Reports", breadcrumbs: ["Gift Card", "Reports"] },
  "/gift-kard/settings": { title: "Gift Card - Settings", breadcrumbs: ["Gift Card", "Settings"] },

// ---------------- Lottery (NEW) ----------------
"/lottery/dashboard": { 
  title: "Lottery - Dashboard", 
  breadcrumbs: ["Lottery", "Dashboard"] 
},

"/lottery/games": { 
  title: "Lottery - Games", 
  breadcrumbs: ["Lottery", "Games"] 
},

"/lottery/games/create": { 
  title: "Lottery - Create Game", 
  breadcrumbs: ["Lottery", "Games", "Create"] 
},

"/lottery/games/:id/update": { 
  title: "Lottery - Update Game", 
  breadcrumbs: ["Lottery", "Games", "Update"] 
},

"/lottery/games/:id/sales": {
  title: "Lottery - Game Sales",
  breadcrumbs: ["Lottery", "Games", "Sales"]
},

"/lottery/games/:id/inventory": {
  title: "Lottery - Game Inventory",
  breadcrumbs: ["Lottery", "Games", "Inventory"]
},

"/lottery/games/:id/view": { 
  title: "Lottery - View Game", 
  breadcrumbs: ["Lottery", "Games", "View"] 
},

"/lottery/daily-closing": { 
  title: "Lottery - Daily Closing", 
  breadcrumbs: ["Lottery", "Daily Closing"] 
},

"/lottery/receive-inventory": { 
  title: "Lottery - Receive Inventory", 
  breadcrumbs: ["Lottery", "Receive Inventory"] 
},

"/lottery/audit": { 
  title: "Lottery - Audit", 
  breadcrumbs: ["Lottery", "Audit"] 
},

"/lottery/audit/create": {
  title: "Lottery - Create Audit",
  breadcrumbs: ["Lottery", "Audit", "Create"]
},

"/lottery/reports": { 
  title: "Lottery - Reports", 
  breadcrumbs: ["Lottery", "Reports"] 
},

"/lottery/settings": { 
  title: "Lottery - Settings", 
  breadcrumbs: ["Lottery", "Settings"] 
},

// ---------------- Sales ----------------
"/sales/dashboard": {
  title: "Sales - Dashboard",
  breadcrumbs: ["Sales", "Dashboard"]
},
"/sales/online-store": {
  title: "Sales - Online Store",
  breadcrumbs: ["Sales", "Online Store"]
},
"/sales/reports": {
  title: "Sales - Reports",
  breadcrumbs: ["Sales", "Reports"]
},
"/sales/orders": {
  title: "Sales - Orders",
  breadcrumbs: ["Sales", "Orders"]
},
"/sales/settings": {
  title: "Sales - Settings",
  breadcrumbs: ["Sales", "Settings"]
},

// ---------------- Analytics ----------------
"/analytics": {
  title: "Analytics",
  breadcrumbs: ["Analytics"]
},
"/analytics/generate-ai": {
  title: "Analytics - Generate with AI",
  breadcrumbs: ["Analytics", "Generate with AI"]
},
"/analytics/templates": {
  title: "Analytics - Templates",
  breadcrumbs: ["Analytics", "Templates"]
},
"/analytics/build-own": {
  title: "Analytics - Build Your Own",
  breadcrumbs: ["Analytics", "Build Your Own"]
},
"/analytics/reports/build-own": {
  title: "Analytics - Build Your Own",
  breadcrumbs: ["Analytics", "Build Your Own"]
},
"/analytics/reports/:reportId": {
  title: "Analytics - View Report",
  breadcrumbs: ["Analytics", "Reports", "View"]
},
"/analytics/reports/:reportId/edit": {
  title: "Analytics - Edit Report",
  breadcrumbs: ["Analytics", "Reports", "Edit"]
},

// ---------------- App Settings ----------------
"/account": {
  title: "Account",
  breadcrumbs: ["Account"]
},
"/settings": {
  title: "Settings",
  breadcrumbs: ["Settings"]
},

// ---------------- Wine Sol ----------------
"/wine-sol/customers": {
  title: "Wine Sol - Customers",
  breadcrumbs: ["Wine Sol", "Customers"]
},
"/wine-sol/customers/:id": {
  title: "Wine Sol - Customer Details",
  breadcrumbs: ["Wine Sol", "Customers", "Details"]
},
"/wine-sol/segments": {
  title: "Wine Sol - Segments",
  breadcrumbs: ["Wine Sol", "Segments"]
},
"/wine-sol/segments/create": {
  title: "Wine Sol - Create Segment",
  breadcrumbs: ["Wine Sol", "Segments", "Create"]
},
"/wine-sol/segments/:id/update": {
  title: "Wine Sol - Update Segment",
  breadcrumbs: ["Wine Sol", "Segments", "Update"]
},
"/wine-sol/segments/:id/send-sms": {
  title: "Wine Sol - Send SMS",
  breadcrumbs: ["Wine Sol", "Segments", "Send SMS"]
},
"/wine-sol/segments/:id/customers": {
  title: "Wine Sol - Segment Customers",
  breadcrumbs: ["Wine Sol", "Segments", "Customers"]
},
"/wine-sol/segments/:id/automations": {
  title: "Wine Sol - Segment Automations",
  breadcrumbs: ["Wine Sol", "Segments", "Automations"]
},
"/wine-sol/automations": {
  title: "Wine Sol - Automations",
  breadcrumbs: ["Wine Sol", "Automations"]
},
"/wine-sol/automations/create": {
  title: "Wine Sol - Create Automation",
  breadcrumbs: ["Wine Sol", "Automations", "Create"]
},
"/wine-sol/automations/:automationId/edit": {
  title: "Wine Sol - Edit Automation",
  breadcrumbs: ["Wine Sol", "Automations", "Edit"]
},
"/wine-sol/marketing/website": {
  title: "Wine Sol - Website",
  breadcrumbs: ["Wine Sol", "Marketing", "Website"]
},
"/wine-sol/marketing/dashboard": {
  title: "Wine Sol - Marketing Dashboard",
  breadcrumbs: ["Wine Sol", "Marketing", "Dashboard"]
},
"/wine-sol/marketing/analytics": {
  title: "Wine Sol - Marketing Analytics",
  breadcrumbs: ["Wine Sol", "Marketing", "Analytics"]
},
"/wine-sol/marketing/communication/dashboard": {
  title: "Wine Sol - Communication Dashboard",
  breadcrumbs: ["Wine Sol", "Marketing", "Communication", "Dashboard"]
},
"/wine-sol/marketing/communication/send-sms": {
  title: "Wine Sol - Communication SMS",
  breadcrumbs: ["Wine Sol", "Marketing", "Communication", "Send SMS"]
},
"/wine-sol/marketing/communication/send-email": {
  title: "Wine Sol - Communication Email",
  breadcrumbs: ["Wine Sol", "Marketing", "Communication", "Send Email"]
},
"/wine-sol/marketing/communication/post": {
  title: "Wine Sol - Compose Post",
  breadcrumbs: ["Wine Sol", "Marketing", "Communication", "Post"]
},
"/wine-sol/marketing/communication/analytics": {
  title: "Wine Sol - Communication Analytics",
  breadcrumbs: ["Wine Sol", "Marketing", "Communication", "Analytics"]
},
"/wine-sol/marketing/communication/campaigns": {
  title: "Wine Sol - Communication Campaigns",
  breadcrumbs: ["Wine Sol", "Marketing", "Communication", "Campaigns"]
},
"/wine-sol/loyalty/customers": {
  title: "Wine Sol - Loyalty Customers",
  breadcrumbs: ["Wine Sol", "Loyalty", "Customers"]
},
"/wine-sol/loyalty/ucp": {
  title: "Wine Sol - UCP",
  breadcrumbs: ["Wine Sol", "Loyalty", "UCP"]
},
"/wine-sol/loyalty/segments": {
  title: "Wine Sol - Loyalty Segments",
  breadcrumbs: ["Wine Sol", "Loyalty", "Segments"]
},
"/wine-sol/loyalty/segments/create": {
  title: "Wine Sol - Loyalty Create Segment",
  breadcrumbs: ["Wine Sol", "Loyalty", "Segments", "Create"]
},
"/wine-sol/loyalty/segments/:id/update": {
  title: "Wine Sol - Loyalty Update Segment",
  breadcrumbs: ["Wine Sol", "Loyalty", "Segments", "Update"]
},
"/wine-sol/loyalty/segments/:id/send-sms": {
  title: "Wine Sol - Loyalty Send SMS",
  breadcrumbs: ["Wine Sol", "Loyalty", "Segments", "Send SMS"]
},
"/wine-sol/loyalty/segments/:id/customers": {
  title: "Wine Sol - Loyalty Segment Customers",
  breadcrumbs: ["Wine Sol", "Loyalty", "Segments", "Customers"]
},
"/wine-sol/loyalty/segments/:id/automations": {
  title: "Wine Sol - Loyalty Segment Automations",
  breadcrumbs: ["Wine Sol", "Loyalty", "Segments", "Automations"]
},
"/wine-sol/loyalty/automations": {
  title: "Wine Sol - Loyalty Automations",
  breadcrumbs: ["Wine Sol", "Loyalty", "Automations"]
},
"/wine-sol/loyalty/automations/create": {
  title: "Wine Sol - Loyalty Create Automation",
  breadcrumbs: ["Wine Sol", "Loyalty", "Automations", "Create"]
},
"/wine-sol/loyalty/automations/:automationId/edit": {
  title: "Wine Sol - Loyalty Edit Automation",
  breadcrumbs: ["Wine Sol", "Loyalty", "Automations", "Edit"]
},
"/wine-sol/loyalty/coupons": {
  title: "Wine Sol - Loyalty Coupons",
  breadcrumbs: ["Wine Sol", "Loyalty", "Coupons"]
},
"/wine-sol/sales/ecommerce": {
  title: "Wine Sol - Ecommerce",
  breadcrumbs: ["Wine Sol", "Sales", "Ecommerce"]
},
"/wine-sol/sales/clubs": {
  title: "Wine Sol - Clubs",
  breadcrumbs: ["Wine Sol", "Sales", "Clubs"]
},
"/wine-sol/settings": {
  title: "Wine Sol - Settings",
  breadcrumbs: ["Wine Sol", "Settings"]
},

};

const findRouteMetadata = (pathname) => {
  const pathSegments = pathname.split("/").filter(Boolean);

  for (let len = pathSegments.length; len >= 0; len--) {
    const subPath = "/" + pathSegments.slice(0, len).join("/");

    for (const routePattern in routeMetadata) {
      const patternSegments = routePattern.split("/").filter(Boolean);

      if (patternSegments.length !== len) continue;

      const isMatch = patternSegments.every((seg, idx) =>
        seg.startsWith(":") || seg === pathSegments[idx]
      );

      if (isMatch) {
        return routeMetadata[routePattern];
      }
    }
  }

  return { title: "App", breadcrumbs: ["Home"] };
};

const Layout = () => {
  const { permissions } = useMenuAccess();
  const {RCN, isApiLoaded} = useRCN();
  const {addToast} = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const popupShownCount = useRef(0);
  const [loading, setLoading] = useState(false);
  const { setShowCart, cartItems } = useCart();
  const { pathname, search } = location;
  const queryParams = new URLSearchParams(search);
  const locationUid = queryParams.get("id");
  const editPoId = queryParams.get("edit-po-id");
  const isCreatePOFlow = queryParams.get("create-po") === "true";
  const isCreatePORoute = pathname.includes("/product-catalogs/create-po");
  const isWholeSalesRoute = pathname.startsWith("/whole-sales");
  const isWholeSalesCatalogRoute = pathname.startsWith("/whole-sales/product-catalogs");
  const isPurchaseCatalogRoute = pathname.startsWith("/purchase-management/product-catalogs");
  const isEditPOCatalogRoute =
    Boolean(editPoId) && (isWholeSalesCatalogRoute || isPurchaseCatalogRoute);
  const isAnalyticsRoute = pathname.startsWith("/analytics");
  const localLocationId = localStorage.getItem("locationId");

  // const showCartIcon =
  // location.pathname.startsWith("/inventory") ||
  // location.pathname.startsWith("/purchase-management");
  const showCartIcon = !(
    isAnalyticsRoute
    || isCreatePOFlow
    || isCreatePORoute
    || isEditPOCatalogRoute
    || (isWholeSalesRoute && !isWholeSalesCatalogRoute)
  );

  if (!locationUid || locationUid?.trim()?.length === 0) {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("id", localLocationId);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  } else {
    localStorage.setItem("locationId", locationUid);
  }

  const { initializeRCNs } = useRCN();

  useEffect(() => {
    if (!isCreatePOFlow) return;
    if (pathname === "/product-catalogs/create-po") return;

    const nextParams = new URLSearchParams(search);
    if (!nextParams.get("id") && localLocationId) {
      nextParams.set("id", localLocationId);
    }
    nextParams.set("create-po", "true");

    navigate(`/product-catalogs/create-po?${nextParams.toString()}`, {
      replace: true,
    });
  }, [isCreatePOFlow, pathname, search, localLocationId, navigate]);

  useEffect(() => {
    async function init() {
      try {
        setLoading(true);
        const params = new URLSearchParams(location.search);
        const locationId = params.get("id");
        // Always initialize RCNs, even without locationId (will use fallback)
        await initializeRCNs(locationId);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }

    init();
  }, []);

  useEffect(() => {
    if((!RCN || String(RCN)?.trim()?.length === 0) && popupShownCount.current === 0 && isApiLoaded){
      popupShownCount.current +=1;
      addToast("RCN not found", "Error", "error", 10 * 1000);
    }

  }, [RCN, isApiLoaded])

  if (loading) {
    return (
      <div className="flex h-screen text-black">
        <LoadingWrapper />
      </div>
    );
  }

  const { title, breadcrumbs } = findRouteMetadata(pathname);
  const pathSegments = pathname.split("/").filter(Boolean);

  const breadcrumbItems = breadcrumbs.map((label, index) => ({
    label,
    ...(index !== 0 && {
      command: () => {
        const targetPath = "/" + pathSegments.slice(0, index + 1).join("/");
        navigate(`${targetPath}${search}`);
      },
    }),
  }));

  return (
    <div className="flex h-screen text-black">
      <Sidebar />

      <div className="flex-1 h-screen overflow-y-auto bg-gray-100 relative">
        <header className="sticky top-0 bg-white shadow-md px-4 py-3 z-50">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <BreadCrumb model={breadcrumbItems} className="font-semibold"/>
              {permissions && (permissions?.purchase || permissions?.distribution) && showCartIcon && (
                <div className="flex items-center gap-4 mr-3">
                  <FaShoppingCart
                    size={32}
                    onClick={() => setShowCart(true)}
                    className="text-xl text-primaryBlue"
                  />
                  <div className="bg-primaryBlue h-5 absolute top-2 right-4 w-5 rounded-full text-white flex justify-center items-center">
                    <span className="text-xm font-semibold text-white text-[12px]">{cartItems?.length}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-hidden pt-4 px-4">
          <RouteAccessGuard>
            <Outlet />
          </RouteAccessGuard>
        </main>

        {permissions && (permissions?.purchase || permissions?.distribution) && showCartIcon && <CartPanel />}
      </div>
    </div>
  );
};

export default Layout;
